// A quiet strip above the fields table that only exists when there is something to say — the same
// shape as the "Work in progress" pane, and deliberately NOT a toast — removed job toasts.
import { useState } from "react";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Loader2,
  X,
} from "lucide-react";
import {
  useMergeWatch,
  isWatching,
  type MergeWatch,
} from "../../stores/mergeWatchStore";

// Past this, say so. The re-grid is ~12 s; the wait is the job draining in-flight work on the
// sources first, which can legitimately run tens of minutes if one is mid-build.
const SLOW_MS = 120_000;

/* * "North 40 and Creek 40" — the sources, read the way a person would say them. */
export function sourceList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "these fields";
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

function Running({ w, nowMs }: { w: MergeWatch; nowMs: number }) {
  const slow = nowMs - w.started_at > SLOW_MS;
  return (
    <div className="flex items-start gap-2 rounded border border-line bg-surface px-2.5 py-2 text-[11px]">
      <Loader2 size={12} className="mt-0.5 shrink-0 animate-spin text-accent" />
      <span className="min-w-0">
        <span className="text-fg">
          Combining {sourceList(w.source_names)} into “{w.name}”…
        </span>
        {/*
 Only said once it IS slow — pre-announcing a long wait on a job that usually takes
            seconds would make every merge feel worse than it is.
*/}
        {slow && (
          <span className="block text-faint">
            Still going. A merge waits for any work already running on these
            fields to finish first, which can take a while — you don’t need to
            stay on this page.
          </span>
        )}
      </span>
    </div>
  );
}

function Failed({ w, onDismiss }: { w: MergeWatch; onDismiss: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex items-start gap-2 rounded border border-red-500/40 bg-red-500/5 px-2.5 py-2 text-[11px]">
      <AlertTriangle size={12} className="mt-0.5 shrink-0 text-red-400" />
      <span className="min-w-0 flex-1">
        <span className="text-fg">
          “{w.name}” couldn’t be combined. Nothing was changed —{" "}
          {sourceList(w.source_names)}{" "}
          {w.source_names.length === 1 ? "is" : "are"} intact and still usable.
        </span>
        {/*
 compute's raw reason: technical, and theirs, so it sits behind a toggle rather than
            being dressed up as a sentence — but it is always reachable.
*/}
        {w.failure_reason && (
          <>
            <button
              onClick={() => setOpen((v) => !v)}
              className="mt-0.5 flex items-center gap-0.5 text-faint hover:text-muted"
            >
              {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}{" "}
              Technical detail
            </button>
            {open && (
              <span className="mt-0.5 block break-words font-mono text-[10px] text-faint">
                {w.failure_reason}
              </span>
            )}
          </>
        )}
      </span>
      <button
        onClick={onDismiss}
        className="shrink-0 rounded p-0.5 text-faint hover:bg-hover hover:text-fg"
        aria-label="Dismiss"
      >
        <X size={12} />
      </button>
    </div>
  );
}

export function MergeWatchStrip() {
  const byId = useMergeWatch((s) => s.byId);
  const dismiss = useMergeWatch((s) => s.dismiss);
  const rows = Object.values(byId);
  // The elapsed clock is read at render. The watcher polls every 5 s and writes status on each tick
  // which re-renders this — so the "still going" line appears without a second timer of its own.
  const nowMs = Date.now();
  if (!rows.length) return null;
  return (
    <div className="flex flex-col gap-1.5">
      {rows.map((w) =>
        isWatching(w) ? (
          <Running key={w.merge_id} w={w} nowMs={nowMs} />
        ) : (
          <Failed
            key={w.merge_id}
            w={w}
            onDismiss={() => dismiss(w.merge_id)}
          />
        ),
      )}
    </div>
  );
}
